
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SuggestionsRequest {
  topic: string;
  grade: string;
  writingType: string;
}

interface SuggestionsResponse {
  details: string[];
  vocabulary: string[];
}

export const useWritingSuggestions = () => {
  const [isLoading, setIsLoading] = useState(false);

  const getSuggestions = async (request: SuggestionsRequest): Promise<SuggestionsResponse> => {
    setIsLoading(true);
    
    try {
      const { data, error } = await supabase.functions.invoke('get-writing-suggestions', {
        body: request
      });

      if (error) {
        throw new Error(error.message);
      }

      return data;
    } catch (error) {
      console.error('Writing suggestions error:', error);
      // Fallback to default suggestions if AI fails
      return getDefaultSuggestions(request);
    } finally {
      setIsLoading(false);
    }
  };

  const getDefaultSuggestions = (request: SuggestionsRequest): SuggestionsResponse => {
    const gradeLevel = parseInt(request.grade);
    
    if (request.writingType.toLowerCase() === 'creative') {
      return {
        details: [
          "Describe the main character(s) and what they look like",
          "Tell us about the setting - where and when does your story happen?",
          "What problem or challenge does your character face?",
          "How do the characters feel throughout the story?",
          "What happens at the beginning, middle, and end?"
        ],
        vocabulary: gradeLevel <= 3 
          ? ["exciting", "brave", "scared", "happy", "colorful", "big", "small", "friendly", "magical"]
          : ["adventurous", "courageous", "mysterious", "enormous", "brilliant", "magnificent", "determined", "enchanted", "spectacular"]
      };
    } else if (request.writingType.toLowerCase() === 'opinion') {
      return {
        details: [
          "State your opinion clearly at the beginning",
          "Give at least 2-3 reasons why you think this way",
          "Use examples from your own experience",
          "Explain why others might disagree",
          "End by restating your opinion"
        ],
        vocabulary: gradeLevel <= 3
          ? ["I think", "because", "best", "good", "bad", "like", "favorite", "important", "better"]
          : ["believe", "opinion", "convince", "argument", "evidence", "furthermore", "however", "therefore", "significant"]
      };
    } else {
      return {
        details: [
          "Start with an interesting fact or question",
          "Organize your information into main topics",
          "Use specific details and examples",
          "Explain things clearly for your reader",
          "End with a summary or conclusion"
        ],
        vocabulary: gradeLevel <= 3
          ? ["first", "next", "then", "also", "important", "because", "example", "different", "same"]
          : ["introduction", "furthermore", "specifically", "evidence", "conclusion", "significant", "analyze", "demonstrate", "illustrate"]
      };
    }
  };

  return {
    getSuggestions,
    isLoading
  };
};
